
import {throwError as observableThrowError, Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import {RequestService} from '../request/request.service';
import {TokenService} from '../../local/token/token.service';

import {SessionService} from '../../local/session/session.service';
import {WebsocketService} from '../websocket/websocket.service';
import {catchError, first, map} from 'rxjs/operators';
import {LocalStorageService} from 'ngx-webstorage';

import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser';
import { HttpHeaders } from '@angular/common/http';

@Injectable()
export class AuthService {
  apiUrl = environment.apiRoot;

  constructor(
    private reqService: RequestService,
    private tokenService: TokenService,
    private sessionService: SessionService,
    private websocketService: WebsocketService,
    private localstorageService: LocalStorageService,
    private msalAuthService: MsalService) {
  }

  login(credentials) {
    const req = {
      url: this.apiUrl + 'Account/Login',
      data: credentials,
      config: {
        waitForCompletion: false,
        successToast: false
      }
    };

    return this.reqService.postRequest(req).pipe(
      map((result: any) => {
        result.keepMeLoggedIn = credentials.keepMeLoggedIn;
        this.tokenService.setTokens(result);
        return result;
      }),
      catchError( err => {
        return observableThrowError(err);
      }));
  }

  reLogin(credentials) {
    const req = {
      url: this.apiUrl + 'Account/RefreshToken',
      data: credentials,
      config: {
        waitForCompletion: false,
        successToast: false
      }
    };

    return this.reqService.postRequest(req).pipe(
      map((result: any) => {
        result.keepMeLoggedIn = this.tokenService.getKeepMeLoggedIn() || false;
        this.tokenService.setTokens(result);
        this.websocketService.initService();
        return result;
      }),
      catchError( err => {
        return observableThrowError(err);
      }));
  }

  logout() {
    return new Observable(observer => {
      this.tokenService.removeTokens();
      this.websocketService.cancelService(this.websocketService.linaConnection);
      observer.next(true);
      observer.complete();
    });
  }

  whoAmI() {
    const req = {
      url: this.apiUrl + 'Account/Me'
    };

    return this.reqService.getRequest(req).pipe(
      map(result => {
        this.sessionService.setUser(result).subscribe(user => {
          return user;
        });
      }),
      catchError(err => {
        return observableThrowError(err);
      }));
  }

  pretend(object) {
    const req = {
      url: this.apiUrl + 'Account/Pretend',
      options: {
        params: object
      },
      config: {
        waitForCompletion: false
      }
    };

    return this.reqService.postRequest(req);
  }

  updateUserInfo(object) {
    const req = {
      url: this.apiUrl + 'Account/User/UpdateUserInfo',
      data: object
    };

    return this.reqService.putRequest(req);
  }

  changePassword(object) {
    const req = {
      url: this.apiUrl + 'Account/ChangePassword',
      data: object
    };

    return this.reqService.postRequest(req);
  }

  resetPassword(object) {
    const req = {
      url: this.apiUrl + 'Account/ResetPassword',
      data: object
    };

    return this.reqService.postRequest(req);
  }

  changePinCode(object) {
    const req = {
      url: this.apiUrl + 'Account/PinCode',
      data: object
    };

    return this.reqService.putRequest(req);
  }

  finishRegistration(object) {
    const req = {
      url: this.apiUrl + 'Account/FinishRegistration',
      data: object,
      config: {
        waitForCompletion: false
      }
    };

    return this.reqService.postRequest(req);
  }

  requestResetToken(object) {
    const req = {
      url: this.apiUrl + 'Account/PasswordToken',
      data: object,
      config: {
        waitForCompletion: false
      }
    };

    return this.reqService.postRequest(req);
  }

  deleteResetToken(id) {
    const req = {
      url: this.apiUrl + 'Account/PasswordToken/' + id,
      config: {
        waitForCompletion: false
      }
    };

    return this.reqService.deleteRequest(req);
  }

  finishResetToken(id, object) {
    const req = {
      url: this.apiUrl + 'Account/PasswordToken/' + id,
      data: object,
      config: {
        waitForCompletion: false
      }
    };

    return this.reqService.putRequest(req);
  }

  createPretendSession(userId) {
    return new Observable(observer => {
      return this.pretend({UserId: userId}).pipe(first()).subscribe(pretendUser => {
        this.localstorageService.store('orgTokens', this.localstorageService.retrieve('tokens'));
        return this.logout().pipe(first()).subscribe(() => {
          this.tokenService.setTokens(pretendUser);
          setTimeout(() => {
            this.websocketService.initService();
          }, 4000);
          observer.next(true);
          observer.complete();
        });
      });
    });
  }

  cancelPretendSession() {
    return new Observable(observer => {
      return this.logout().pipe(first()).subscribe(pretendUser => {
        this.tokenService.setTokens(this.localstorageService.retrieve('orgTokens'));
        this.localstorageService.clear('orgTokens');
        setTimeout(() => {
          this.websocketService.initService();
        }, 4000);
        observer.next(true);
        observer.complete();
      });
    });
  }

  gsxToken(object) {
    const req = {
      url: this.apiUrl + 'Account/User/GsxRestToken',
      data: object
    };

    return this.reqService.putRequest(req);
  }

  requestChangeUsernameToken(object) {
    const req = {
      url: this.apiUrl + 'Account/UserNameChangeToken',
      data: object,
    };

    return this.reqService.postRequest(req);
  }

  confirmChangeUsername(token) {
    const req = {
      url: this.apiUrl + 'Account/UserNameChangeToken/' + token
    };

    return this.reqService.putRequest(req);
  }

  getSessions() {
    const req = {
      url: this.apiUrl + 'Account/Sessions',
    };

    return this.reqService.getRequest(req);
  }

  removeSessions(token) {
    const req = {
      url: this.apiUrl + 'Account/Sessions/' + token,
    };

    return this.reqService.deleteRequest(req);
  }

  loginMsal(){
    return new Observable((observer) => {
      let account = this.msalAuthService.instance.getAllAccounts()

      let ssoRequest = account.length == 1 ? 
      {
        scopes: environment.msalConfig.loginScopes,
        account: account[0]
      }:
      { 
        scopes: environment.msalConfig.loginScopes,
      };

      this.msalAuthService.ssoSilent(ssoRequest).subscribe(
        (ssoResult) => {
          //Note: Assumption that we only have one account, hence we are using index 0
          let accessTokenRequest = {
            scopes: environment.msalConfig.loginScopes,
            account: this.msalAuthService.instance.getAllAccounts()[0]
          };
          this.msalAuthService.acquireTokenSilent(accessTokenRequest).subscribe((accessTokenResponse) =>{
            observer.next(accessTokenResponse);
            observer.complete();
          },
          (rejected) => {
            if(rejected instanceof InteractionRequiredAuthError)
            {
              //If we get an interaction required error, and want to have sso signing within Lina
              // we should try to acquire a token with a popup, as of now we are not doing this
              // this.msalAuthService.acquireTokenPopup(accessTokenRequest).subscribe((accessTokenResponse) =>{
              //   //return accessTokenResponse;
              //   observer.next(accessTokenResponse);
              //   observer.complete();
              // },(rejected) =>{
              //   //return rejected;
              //   observer.error(rejected);
              //   observer.complete();
              // });
            }
          });
        },(ssoReject) => {
          //If we get an interaction required error, and want to have sso signing within Lina
          // we should try to acquire a token with a popup, as of now we are not doing this
          if(ssoReject instanceof InteractionRequiredAuthError)
          {
            console.log(ssoReject);
            let popupRequest = 
            {
              scopes: environment.msalConfig.loginScopes,
              prompt: "select_account"
            }
            this.msalAuthService.handleRedirectObservable().subscribe({
              next: (result: AuthenticationResult) => {
                if(result !== null)
                {
                  observer.next(result);
                  observer.complete();
                }
                else{
                  this.msalAuthService.loginRedirect(popupRequest)
                }
              },
              error: (error) =>
              {
                observer.error(error);
                observer.complete();
                console.log(error);
              } 
            });
          }
          else{
            observer.error(ssoReject);
            observer.complete();
          }
        });
    });
  }
  getUserFromMsalLogin(username: string | undefined, accessToken: string)
  {
      return new Observable((observer) => {
        const customHeaders = new HttpHeaders({
          'Authorization': ('Bearer ' + accessToken),
        });

        const req = {
          url: this.apiUrl + 'Account/FromMsalToken?userName=' + username,
          options:{
            headers: customHeaders,
            method: "GET"
          }
        };

        this.reqService.getRequest(req).subscribe(
          (result) => {
            this.tokenService.setTokens(result);
            observer.next(result);
            observer.complete();
          },
          (reject) => {
            observer.error(reject);
            observer.complete()
          });
      });
  }
}
